import React, { useState, useEffect, useContext } from "react";
import { useFormikContext } from "formik";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import DialogTitle from "@mui/material/DialogTitle";
import Step0 from "./Step0";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import useAuth from "src/hooks/useAuth";
import { ReceiverInventoryMoveContext } from "../../MoveLocationsModal/ReceiverInventoryMove";

export default function InventoryMoveAssistant({ projectId, shipmentItem, shipment, onClose }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const initialStep = settings.receiving_into_pallets ? 3 : 0;
  const [step, setStep] = useState(initialStep);
  const { values, setFieldValue } = useFormikContext();
  const { autoSelectLocations } = useContext(ReceiverInventoryMoveContext);

  const handleNext = () => {
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleMoveToSingleLocation = () => {
    const [location] = values.locations;
    location.sub_qty = shipmentItem.quantity;
    setFieldValue("locations", [location]);
    setStep(3);
  };

  const handleUpdateLocations = locations => {
    setFieldValue(
      "locations",
      values.locations.map((location, index) => ({
        ...location,
        ...locations[index],
      }))
    );
    onClose();
  };

  const handleMoveToMultipleLocations = (count, quantity = 0) => {
    const [location] = values.locations;

    const newLocations = [...Array(count)].map(() => ({
      ...location,
      sub_qty: quantity,
      warehouse_locations: [],
    }));

    setFieldValue("locations", newLocations);
    handleNext();
  };

  useEffect(() => {
    if (values.locations) {
      const [location] = values.locations
        .filter(loc => loc.shipment_id === shipment.shipmentId)
        .map(location => ({
          ...location,
          warehouse: autoSelectLocations?.warehouse || location.warehouse,
          // Clear warehouse_locations for move operations unless autoSelectLocations provides them
          // This allows the user to select a new destination location
          // mobile_warehouse_location is preserved unless explicitly changed by user
          warehouse_locations: autoSelectLocations?.warehouse_locations || [],
        }));

      if (settings.receiving_into_pallets) {
        location.sub_qty = shipmentItem.quantity;
      }

      setFieldValue("locations", [location]);

      if (autoSelectLocations) {
        onClose();
      }
    }
  }, [settings.receiving_into_pallets]);

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 600,
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <Typography
          align="center"
          color="textSecondary"
          sx={{ px: 2, fontWeight: 500, fontSize: 22 }}
        >
          Inventory Move Assistant
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {step === 0 && <Step0 onNext={handleNext} />}
      {step === 1 && (
        <Step1
          total={shipmentItem.quantity}
          onNext={handleNext}
          onBack={handleBack}
          onMoveToSingleLocation={handleMoveToSingleLocation}
        />
      )}
      {step === 2 && (
        <Step2
          onBack={handleBack}
          onMoveToMultipleLocations={handleMoveToMultipleLocations}
          quantity={shipmentItem.quantity}
          setFieldValue={setFieldValue}
        />
      )}
      {step === 3 && (
        <Step3
          projectId={projectId}
          onBack={handleBack}
          onUpdateLocations={handleUpdateLocations}
          locations={values.locations}
          shipmentItem={shipmentItem}
        />
      )}
    </Dialog>
  );
}
